// supabase/functions/_shared/booking/slots.ts
// Free demo slots (spec booking §2): every 30-minute start on a 15-minute grid (:00, :15, :30, :45) inside the rep's
// weekly hours, in the rep's zone; at least noticeHours from now and before now + horizonDays; Dec 25, Jan 1 and the
// holidays list skipped; free when [start, start + 30) misses every busy interval widened by 15 minutes on both sides.
// Pure. Shared vectors: slot-vectors.json. Also used by the demo build (fakePublicBookingApi.ts).
import { wallToUtc, zoneParts } from "../gavion/time/zone.ts";
import type { IsoDay, WeeklyHours } from "./settings.ts";

export const SLOT_MINUTES = 30;
export const GRID_MINUTES = 15;
export const BUFFER_MINUTES = 15;
const MIN = 60_000;
const HOUR = 3_600_000;
const DAY = 86_400_000;

export type SlotRules = {
  tz: string;
  weeklyHours: WeeklyHours;
  noticeHours: number;
  horizonDays: number;
  /** YYYY-MM-DD local dates (the email holidays: Quebec, or the admin's list). */
  holidays: readonly string[];
};
export type BusyInterval = { start: string; end: string };

const pad = (n: number) => String(n).padStart(2, "0");
const minutesOf = (hhmm: string): number => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

/** Dec 25 and Jan 1, every year: twin of private.dialer_holiday (47_gavion_holiday_block.sql). */
export const isFixedHoliday = (day: string): boolean =>
  day.endsWith("-12-25") || day.endsWith("-01-01");

export function computeSlots(i: {
  rules: SlotRules;
  busy: readonly BusyInterval[];
  now: Date;
  from: Date;
  to: Date;
  exclude?: readonly string[];
}): string[] {
  const { rules } = i;
  const earliest = Math.max(i.from.getTime(), i.now.getTime() + rules.noticeHours * HOUR);
  const latest = Math.min(i.to.getTime(), i.now.getTime() + rules.horizonDays * DAY); // exclusive
  if (!(latest > earliest)) return [];
  const blocked = i.busy
    .map((b) => ({
      start: Date.parse(b.start) - BUFFER_MINUTES * MIN,
      end: Date.parse(b.end) + BUFFER_MINUTES * MIN,
    }))
    .filter((b) => Number.isFinite(b.start) && Number.isFinite(b.end) && b.end > b.start);
  const excluded = new Set(
    (i.exclude ?? []).map((s) => Date.parse(s)).filter(Number.isFinite),
  );
  const holidays = new Set(rules.holidays);
  const out = new Set<number>();
  // Every local calendar day that can touch [earliest, latest), one day of margin on each side.
  const a = zoneParts(new Date(earliest - DAY), rules.tz);
  const b = zoneParts(new Date(latest + DAY), rules.tz);
  const lastDay = Date.UTC(b.year, b.month - 1, b.day);
  for (let d = Date.UTC(a.year, a.month - 1, a.day); d <= lastDay; d += DAY) {
    const day = new Date(d).toISOString().slice(0, 10);
    if (holidays.has(day) || isFixedHoliday(day)) continue;
    const weekday = String(((new Date(d).getUTCDay() + 6) % 7) + 1) as IsoDay;
    for (const [s, e] of rules.weeklyHours[weekday] ?? []) {
      const end = minutesOf(e);
      const first = Math.ceil(minutesOf(s) / GRID_MINUTES) * GRID_MINUTES;
      for (let m = first; m + SLOT_MINUTES <= end; m += GRID_MINUTES) {
        const at = wallToUtc(d + m * MIN, rules.tz).getTime();
        const p = zoneParts(new Date(at), rules.tz);
        // A wall time in the spring-forward gap does not exist: skip it (no shifted duplicate).
        if (`${p.year}-${pad(p.month)}-${pad(p.day)}` !== day || p.hour * 60 + p.minute !== m) continue;
        if (at < earliest || at >= latest || excluded.has(at)) continue;
        const slotEnd = at + SLOT_MINUTES * MIN;
        if (blocked.some((x) => at < x.end && slotEnd > x.start)) continue;
        out.add(at);
      }
    }
  }
  return [...out].sort((x, y) => x - y).map((t) => new Date(t).toISOString());
}

/** True when `start` is one of the free slots (on the grid, inside the hours, notice, horizon, not busy). */
export function isBookable(
  rules: SlotRules,
  busy: readonly BusyInterval[],
  now: Date,
  start: string,
): boolean {
  const t = Date.parse(start);
  if (!Number.isFinite(t)) return false;
  return computeSlots({ rules, busy, now, from: new Date(t), to: new Date(t + MIN) }).includes(
    new Date(t).toISOString(),
  );
}
